/**
 * AuthProvider: estado de autenticación + hook useAuth.
 *
 * Estado:
 *  - status: "loading" mientras revisa tokens, "authenticated" o "guest" después.
 *  - me: perfil del usuario autenticado (null si guest).
 *
 * Acciones:
 *  - login(payload): autentica y carga el perfil.
 *  - logout(): limpia tokens y estado.
 *  - refreshMe(): vuelve a cargar /me (útil después de cambiar contraseña, MFA, etc.).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  type LoginRequest,
  type MeResponse,
  getMe,
  login as apiLogin,
  logout as apiLogout,
} from "../api/auth";
import { clearTokens, getTokens, setTokens } from "./tokens";

type Status = "loading" | "authenticated" | "guest";

interface AuthState {
  status: Status;
  me: MeResponse | null;
  login: (payload: LoginRequest) => Promise<MeResponse>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<MeResponse | null>(null);

  // Carga inicial: si hay tokens guardados, intenta hidratar el perfil.
  useEffect(() => {
    const tokens = getTokens();
    if (!tokens) {
      setStatus("guest");
      return;
    }
    getMe()
      .then((profile) => {
        setMe(profile);
        setStatus("authenticated");
      })
      .catch(() => {
        clearTokens();
        setMe(null);
        setStatus("guest");
      });
  }, []);

  const login = useCallback(async (payload: LoginRequest): Promise<MeResponse> => {
    const tokens = await apiLogin(payload);
    setTokens({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      access_expires_at: tokens.access_expires_at,
      refresh_expires_at: tokens.refresh_expires_at,
    });
    const profile = await getMe();
    setMe(profile);
    setStatus("authenticated");
    return profile;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // logout es best-effort; aunque falle limpiamos local
    }
    clearTokens();
    setMe(null);
    setStatus("guest");
  }, []);

  const refreshMe = useCallback(async () => {
    const profile = await getMe();
    setMe(profile);
  }, []);

  return (
    <AuthContext.Provider value={{ status, me, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
